import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Lock, ArrowRight, AlertOctagon, AlertTriangle, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { startCheckout } from "@/pages/PaymentPages";
import { ScoreRing } from "@/components/ScoreBadge";

const FAKE_LINES = [
  "Notre gamme éco-responsable adopte une démarche 100% naturelle et durable pour vos clients.",
  "Emballage entièrement recyclable — un geste concret pour la planète.",
  "Neutre en carbone depuis 2024 grâce à notre programme de compensation certifié.",
  "Fabriqué avec des matériaux biosourcés et une production zéro déchet.",
  "Certifié respectueux de l'environnement par notre engagement Green Tech.",
  "Nos ingrédients d'origine végétale garantissent un impact zéro sur la biodiversité.",
];

export default function FreeScanReport() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useLang();
  const { user } = useAuth();
  const [report, setReport] = useState(null);

  useEffect(() => {
    const cached = localStorage.getItem("empco_last_scan");
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed.id === id) { setReport(parsed); return; }
    }
    api.get(`/scan/free/${id}`).then(r => setReport(r.data)).catch(() => toast.error(t("scan.error")));
  }, [id, t]);

  const handleUnlock = async () => {
    if (report.source === "image" || report.source === "document") {
      const dest = report.source === "image" ? "/app/image-scan" : "/app/document-scan";
      if (!user) { nav(`/register?next=${report.source}`); return; }
      nav(dest);
      return;
    }
    if (!user) { nav(`/register?next=full&url=${encodeURIComponent(report.url)}`); return; }
    try {
      toast.loading(t("pay.redirect"), { id: "full" });
      if (user.plan === "surveillance") {
        const { data } = await api.post("/scan/full", { url: report.url });
        toast.success(t("pay.done"), { id: "full" });
        nav(`/app/scan/${data.id}`);
        return;
      }
      await startCheckout("scan_full", report.url);
    } catch (e) {
      toast.error(t("scan.error"), { id: "full" });
    }
  };

  if (!report) return (
    <div className="min-h-screen"><Header variant="public" /><div className="max-w-4xl mx-auto p-10 font-mono text-sm text-muted-foreground">Chargement…</div></div>
  );

  return (
    <div className="App min-h-screen">
      <Header variant="public" />
      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        <button onClick={() => nav("/")} className="text-sm text-muted-foreground hover:text-foreground mb-6 font-mono" data-testid="report-back">← {t("common.back")}</button>

        <div className="grid md:grid-cols-12 gap-8">
          {/* Left: summary */}
          <div className="md:col-span-5 md:sticky md:top-24 self-start">
            <p className="eyebrow mb-2">{report.source === "image" ? "Analyse image gratuite" : report.source === "document" ? "Analyse document gratuite" : "Scan gratuit"} · {new Date(report.scanned_at).toLocaleString()}</p>
            <h1 className="text-3xl md:text-3xl sm:text-4xl font-serif tracking-tight mb-3 break-all" data-testid="report-url">{report.url}</h1>
            <p className="text-sm text-muted-foreground mb-8 font-mono">{report.source === "image" ? `Image · ${report.total_words || 0} mots` : report.source === "document" ? `Document · ${report.total_words || 0} mots${report.pages_scanned ? ` · ${report.pages_scanned} pages` : ""}` : `${report.pages_scanned} ${t("report.pages")}`} · lang: {report.lang?.toUpperCase()}</p>

            <div className="hairline p-5 mb-3 flex items-center gap-6" data-testid="metric-score">
              <ScoreRing score={report.compliance_score ?? 0} size={110} stroke={9} />
              <div>
                <p className="eyebrow mb-1">{t("score.title")}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{t("score.hint")}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-8">
              <div className="hairline p-5 border-t-4 border-t-[hsl(var(--status-red))]" data-testid="metric-red">
                <div className="flex items-center gap-2 mb-3"><AlertOctagon className="w-4 h-4 text-[hsl(var(--status-red))]" /><p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t("report.red")}</p></div>
                <p className="text-5xl font-serif text-[hsl(var(--status-red))]">{report.red_count}</p>
              </div>
              <div className="hairline p-5 border-t-4 border-t-[hsl(var(--status-orange))]" data-testid="metric-orange">
                <div className="flex items-center gap-2 mb-3"><AlertTriangle className="w-4 h-4 text-[hsl(29_87%_50%)]" /><p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t("report.orange")}</p></div>
                <p className="text-5xl font-serif text-[hsl(29_87%_50%)]">{report.orange_count}</p>
              </div>
            </div>

            <div className="hairline bg-primary text-primary-foreground p-6 rounded-lg">
              <div className="flex items-start gap-3 mb-4">
                <Lock className="w-4 h-4 mt-0.5 text-[hsl(var(--status-orange))] shrink-0" />
                <p className="text-sm leading-relaxed">{t("report.blur")}</p>
              </div>
              <Button onClick={handleUnlock} className="w-full h-12 rounded-lg bg-white text-primary hover:bg-white/90" data-testid="unlock-button">
                {t("report.unlock")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="mt-3 text-[11px] font-mono text-primary-foreground/65 text-center">{t("pay.secure")}</p>
            </div>
          </div>

          {/* Right: blurred detailed report preview */}
          <div className="md:col-span-7">
            <div className="hairline p-6 relative" data-testid="blurred-report">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  <p className="font-mono text-xs uppercase tracking-widest">{t("report.title")} · exemples</p>
                </div>
                <span className="tag-red px-2 py-1 text-[10px] font-mono uppercase rounded-lg">Aperçu</span>
              </div>

              {/* Preview claims (2 unblurred as teaser) */}
              <div className="space-y-4 mb-6">
                {(report.preview_claims || []).map((p, i) => (
                  <div key={p.id || `${p.term}-${p.excerpt?.slice(0,40)}`} className="flex items-start gap-3 p-4 bg-muted/40 rounded-lg border border-border" data-testid={`preview-claim-${i}`}>
                    <span className={`px-1.5 h-5 text-[10px] font-mono flex items-center rounded-lg ${p.severity === "red" ? "tag-red" : "tag-orange"}`}>
                      {p.severity.toUpperCase()}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm"><span className="font-serif italic">« {p.excerpt} »</span></p>
                      <p className="text-[11px] font-mono text-muted-foreground mt-1">Terme détecté&nbsp;: <b>{p.term}</b></p>
                    </div>
                  </div>
                ))}
                {!report.preview_claims?.length && (
                  <p className="text-sm text-muted-foreground italic">Aucune allégation détectée dans l'aperçu.</p>
                )}
              </div>

              {/* Blurred fake claims */}
              <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Détails complets ↓</p>
              <div className="relative">
                <div className="blurred-content space-y-4">
                  {FAKE_LINES.map((line) => (
                    <div key={line.slice(0, 40)} className="flex items-start gap-3 p-4 bg-muted/60 rounded-lg border border-border">
                      <span className="px-1.5 h-5 text-[10px] font-mono flex items-center rounded-lg tag-red">RED</span>
                      <div className="flex-1">
                        <p className="text-sm"><span className="font-serif italic">« {line} »</span></p>
                        <p className="text-[11px] font-mono text-muted-foreground mt-2">Art. 3 §1 UE 2024/825 · reformulation suggérée disponible dans le rapport complet.</p>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Overlay CTA */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center bg-white p-7 rounded-xl border border-border shadow-[0_20px_40px_-15px_rgba(31,90,153,0.35)] max-w-sm">
                    <Lock className="w-6 h-6 mx-auto mb-3 text-[hsl(var(--status-red))]" />
                    <p className="font-serif text-lg mb-4">Débloquez la totalité du rapport</p>
                    <Button onClick={handleUnlock} className="w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90" data-testid="unlock-overlay-button">
                      {t("report.unlock")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
